import { requestApiSaga } from '@client/common/redux/api/sagas';
import { reportError } from '@client/common/redux/errors/actions';
import { parseError } from '@client/common/utils/api/error';
import { CostCenters } from '@client/myAccount/costCenters/api';
import { COST_CENTERS_LIST_DEFAULT_PAGE_SIZE } from '@client/myAccount/costCenters/consts';
import {
  fetchCostCenters,
  fetchCostCentersFulfilled,
  fetchCostCentersMisconfigured,
  fetchCostCentersMisconfiguredFulfilled,
  fetchCostCentersRejected,
  fetchCostCentersSearch,
  fetchCostCentersSearchFulfilled,
  fetchCostCentersSearchRejected,
  fetchCostCentersSimpleSearch,
} from '@client/myAccount/costCenters/redux/costCenters';
import { AxiosResponse } from 'axios';
import { all, AllEffect, call, CallEffect, debounce, delay, ForkEffect, put, PutEffect, takeLatest } from 'redux-saga/effects';
import emitter, { EVENT } from 'b2b-apps/core/common/utils/eventEmitter';
import {
  CostCenterSearchListItemType,
  CostCentersType,
} from 'b2b-common/core/account/api/costCenters/CostCenters.types';

const COST_CENTERS_SEARCH_DEBOUNCE = 500;
const COST_CENTERS_MISCONFIGURED_DELAY = 5 * 60 * 1000;

function* fetchCostCentersSaga(action: ReturnType<typeof fetchCostCenters>):
  Generator<CallEffect | PutEffect, void, AxiosResponse<CostCentersType>> {
  const page = action.payload || 1;
  const startItem = (page - 1) * COST_CENTERS_LIST_DEFAULT_PAGE_SIZE;

  try {
    const response = yield call(
      requestApiSaga,
      CostCenters.list(startItem),
    );

    yield put(fetchCostCentersFulfilled(response.data));
  } catch (error) {
    yield put(fetchCostCentersRejected(parseError(error)));
    yield put(reportError({ message: action.type, error, additionalData: { page: action.payload } }));
  }
}

function* fetchCostCentersSearchSaga(action: ReturnType<typeof fetchCostCentersSearch>):
  Generator<CallEffect | PutEffect, void, AxiosResponse<CostCentersType>> {
  const { page = 1, query } = action.payload;
  const startItem = (page - 1) * COST_CENTERS_LIST_DEFAULT_PAGE_SIZE;

  try {
    const response = yield call(
      requestApiSaga,
      CostCenters.search(startItem, COST_CENTERS_LIST_DEFAULT_PAGE_SIZE, query),
    );

    yield put(fetchCostCentersSearchFulfilled(response.data));
  } catch (error) {
    yield put(fetchCostCentersSearchRejected(parseError(error)));
    yield put(reportError({ message: action.type, error, additionalData: action.payload }));
  }
}

function* fetchCostCentersSimpleSearchSaga(action: ReturnType<typeof fetchCostCentersSimpleSearch>):
  Generator<
  CallEffect | PutEffect,
  void,
  AxiosResponse<CostCenterSearchListItemType[]>
> {
  const { query, includeInactive } = action.payload;

  try {
    const response = yield call(
      requestApiSaga,
      CostCenters.simpleSearch({ query, includeInactive }),
    );

    emitter.dispatch(EVENT.searchCostCentersFulfilled, response.data);
  } catch (error) {
    emitter.dispatch(EVENT.searchCostCentersRejected, []);
  }
}

// todo - not used for now
// function* fetchCostCentersMisconfiguredSaga(action: ReturnType<typeof fetchCostCentersMisconfigured>):
//   Generator<
//   CallEffect | PutEffect,
//   void,
//   AxiosResponse<CostCentersMisconfiguredType>
// > {
//   try {
//     const response = yield call(
//       requestApiSaga,
//       CostCenters.getMisconfigured(),
//     );

//     yield put(fetchCostCentersMisconfiguredFulfilled(response.data));
//   } catch (error) {
//     yield put(fetchCostCentersMisconfiguredRejected(parseError(error)));
//     yield put(reportError({ message: action.type, error }));
//   }
// }

function* refreshMisconfiguredCCSaga(): Generator<
  CallEffect | PutEffect,
  void,
  any
  > {
  yield delay(COST_CENTERS_MISCONFIGURED_DELAY);
  yield put(fetchCostCentersMisconfigured());
}

export function* costCentersSaga(): Generator<AllEffect<ForkEffect>, void> {
  yield all([
    takeLatest(fetchCostCenters.type, fetchCostCentersSaga),
    takeLatest(fetchCostCentersSearch.type, fetchCostCentersSearchSaga),
    // todo https://webcompetec.slack.com/archives/C014GGSUXEE/p1747126944106669
    // takeLatest(fetchCostCentersMisconfigured.type, fetchCostCentersMisconfiguredSaga),
    takeLatest(fetchCostCentersMisconfiguredFulfilled.type, refreshMisconfiguredCCSaga),
    debounce(COST_CENTERS_SEARCH_DEBOUNCE, fetchCostCentersSimpleSearch.type, fetchCostCentersSimpleSearchSaga),
  ]);
}
